import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ReactNode } from 'react';

export type ToastTone = 'info' | 'success' | 'warning' | 'critical';

export interface Toast {
  readonly id: string;
  readonly tone: ToastTone;
  readonly title: string;
  readonly description?: string;
  /** Override default dismiss timing in ms. 0 = persistent. */
  readonly durationMs?: number;
}

export interface ToastInput {
  readonly tone?: ToastTone;
  readonly title: string;
  readonly description?: string;
  readonly durationMs?: number;
  /** Idempotency key — pushing the same key twice replaces the prior toast. */
  readonly key?: string;
}

interface ToastApi {
  readonly push: (input: ToastInput) => string;
  readonly dismiss: (id: string) => void;
}

const ToastContext = createContext<ToastApi | null>(null);

const TONE_COLOR: Record<ToastTone, string> = {
  info: 'var(--color-accent)',
  success: 'var(--color-green)',
  warning: 'var(--color-orange)',
  critical: 'var(--color-red)',
};

const TONE_BG: Record<ToastTone, string> = {
  info: 'rgba(0,196,232,0.10)',
  success: 'rgba(46,196,182,0.10)',
  warning: 'rgba(244,128,60,0.10)',
  critical: 'rgba(224,82,82,0.14)',
};

const DEFAULT_DURATION_MS: Record<ToastTone, number> = {
  info: 5_000,
  success: 5_000,
  warning: 5_000,
  // critical stays until dismissed
  critical: 0,
};

export function ToastProvider({ children }: { readonly children: ReactNode }): JSX.Element {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const counter = useRef(0);
  const timers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
    const timer = timers.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timers.current.delete(id);
    }
  }, []);

  const push = useCallback(
    (input: ToastInput): string => {
      const tone = input.tone ?? 'info';
      const id = input.key ?? `t${++counter.current}`;
      const toast: Toast = {
        id,
        tone,
        title: input.title,
        description: input.description,
        durationMs: input.durationMs ?? DEFAULT_DURATION_MS[tone],
      };
      setToasts((prev) => {
        const existing = prev.findIndex((t) => t.id === id);
        if (existing >= 0) {
          const next = [...prev];
          next[existing] = toast;
          return next;
        }
        return [...prev, toast];
      });
      const existingTimer = timers.current.get(id);
      if (existingTimer) clearTimeout(existingTimer);
      if (toast.durationMs && toast.durationMs > 0) {
        const t = setTimeout(() => dismiss(id), toast.durationMs);
        timers.current.set(id, t);
      }
      return id;
    },
    [dismiss],
  );

  useEffect(() => {
    return () => {
      for (const t of timers.current.values()) clearTimeout(t);
      timers.current.clear();
    };
  }, []);

  const api = useMemo<ToastApi>(() => ({ push, dismiss }), [push, dismiss]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>');
  return ctx;
}

function ToastViewport({
  toasts,
  onDismiss,
}: {
  readonly toasts: Toast[];
  readonly onDismiss: (id: string) => void;
}): JSX.Element {
  return (
    <div
      className="fixed bottom-4 right-4 z-50 flex flex-col gap-2 max-w-sm pointer-events-none"
      aria-live="polite"
      aria-atomic="false"
    >
      {toasts.map((t) => (
        <ToastCard key={t.id} toast={t} onDismiss={() => onDismiss(t.id)} />
      ))}
    </div>
  );
}

function ToastCard({
  toast,
  onDismiss,
}: {
  readonly toast: Toast;
  readonly onDismiss: () => void;
}): JSX.Element {
  return (
    <div
      role={toast.tone === 'critical' ? 'alert' : 'status'}
      className="pointer-events-auto rounded-md border p-3 shadow-lg animate-fade-slide-in"
      style={{
        borderColor: TONE_COLOR[toast.tone],
        backgroundColor: TONE_BG[toast.tone],
        color: 'var(--color-text)',
        backdropFilter: 'blur(8px)',
      }}
    >
      <div className="flex items-start gap-2">
        <span
          aria-hidden
          className="mt-1 inline-block h-2 w-2 rounded-full flex-shrink-0"
          style={{ backgroundColor: TONE_COLOR[toast.tone] }}
        />
        <div className="flex-1 min-w-0">
          <p className="font-mono text-xs uppercase tracking-[0.2em]" style={{ color: TONE_COLOR[toast.tone] }}>
            {toast.title}
          </p>
          {toast.description && (
            <p className="mt-1 text-sm leading-snug">{toast.description}</p>
          )}
        </div>
        <button
          type="button"
          onClick={onDismiss}
          className="text-text-dim hover:text-text leading-none px-1"
          aria-label="Dismiss"
        >
          ✕
        </button>
      </div>
    </div>
  );
}
